import { useMemo, useState } from 'react';
import Button from '@/components/Button';
import {
  AGE_RANGES,
  CHARACTERS,
  GENDERS,
  LANGUAGES,
  TONALITIES,
  USE_CASES,
} from '@shared/constants';
import type {
  AgeRange,
  CastingAnswers,
  Character,
  Gender,
  Language,
  Tonality,
  UseCase,
} from '@shared/types';
import styles from './CastingView.module.css';

type QuestionId =
  | 'customerName'
  | 'useCases'
  | 'languages'
  | 'gender'
  | 'ageRange'
  | 'characters'
  | 'tonality';

// Welcome (customerName) ist Step 0, danach 6 Fragen. Finale Gruppierung folgt in Etappe 7.
const QUESTION_ORDER: QuestionId[] = [
  'customerName',
  'useCases',
  'languages',
  'gender',
  'ageRange',
  'characters',
  'tonality',
];

type DraftAnswers = {
  useCases: UseCase[];
  languages: Language[];
  gender: Gender | 'egal' | null;
  ageRange: AgeRange | 'egal' | null;
  characters: Character[];
  tonality: Tonality[];
  customerName: string;
};

const INITIAL: DraftAnswers = {
  useCases: [],
  languages: [],
  gender: null,
  ageRange: null,
  characters: [],
  tonality: [],
  customerName: '',
};

const MAX_CHARACTERS = 5;

export default function Wizard({
  slug: _slug,
  projectName,
  onAnswersComplete,
}: {
  slug: string;
  projectName: string;
  onAnswersComplete: (answers: CastingAnswers) => void;
}) {
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<DraftAnswers>(INITIAL);

  const totalSteps = QUESTION_ORDER.length;
  const questionCount = totalSteps - 1; // ohne Welcome-Screen
  const isWelcome = step === 0;
  const progress = isWelcome ? 0 : Math.round((step / questionCount) * 100);
  const currentId = QUESTION_ORDER[step];

  const isValid = useMemo(() => {
    switch (currentId) {
      case 'useCases':
        return draft.useCases.length > 0;
      case 'languages':
        return draft.languages.length > 0;
      case 'gender':
        return draft.gender !== null;
      case 'ageRange':
        return draft.ageRange !== null;
      case 'characters':
        return draft.characters.length > 0;
      case 'tonality':
        return draft.tonality.length > 0;
      case 'customerName':
        return draft.customerName.trim().length >= 2;
    }
  }, [currentId, draft]);

  function next() {
    if (!isValid) return;
    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      onAnswersComplete({
        useCases: draft.useCases,
        languages: draft.languages,
        gender: draft.gender ?? 'egal',
        ageRange: draft.ageRange ?? 'egal',
        characters: draft.characters,
        tonality: draft.tonality,
        customerName: draft.customerName.trim(),
      });
    }
  }

  function prev() {
    if (step > 0) setStep(step - 1);
  }

  const primaryLabel = isWelcome
    ? "Los geht's"
    : step === totalSteps - 1
      ? 'Vorschläge ansehen'
      : 'Weiter';

  return (
    <>
      <TopBar
        projectName={projectName}
        step={step}
        questionCount={questionCount}
        progress={progress}
        isWelcome={isWelcome}
      />
      <div className={styles.body}>
        <div className={`${styles.centered} ${styles.screen}`} key={step}>
          <QuestionContent
            id={currentId}
            draft={draft}
            setDraft={setDraft}
            projectName={projectName}
          />

          <div className={styles.controls}>
            {!isWelcome && (
              <Button type="button" onClick={prev}>
                Zurück
              </Button>
            )}
            <Button type="button" onClick={next} disabled={!isValid}>
              {primaryLabel}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

function TopBar({
  projectName,
  step,
  questionCount,
  progress,
  isWelcome,
}: {
  projectName: string;
  step: number;
  questionCount: number;
  progress: number;
  isWelcome: boolean;
}) {
  return (
    <div className={styles.topbar}>
      <img src="/brand/tro_logo_pos.svg" alt="TRO" />
      <div className={styles.progressTrack}>
        <div
          className={styles.progressFill}
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className={styles.stepLabel}>
        {isWelcome ? projectName : `Frage ${step} / ${questionCount} · ${projectName}`}
      </div>
    </div>
  );
}

function QuestionContent({
  id,
  draft,
  setDraft,
  projectName,
}: {
  id: QuestionId;
  draft: DraftAnswers;
  setDraft: React.Dispatch<React.SetStateAction<DraftAnswers>>;
  projectName: string;
}) {
  switch (id) {
    case 'customerName':
      return (
        <>
          <div className={styles.questionMeta}>
            Voice Casting für {projectName}
          </div>
          <h1>Welche Stimme passt?</h1>
          <p>
            Beantworte gleich 6 kurze Fragen, höre Vorschläge ab und wähle bis
            zu drei Favoriten aus. Dauert etwa 3–5 Minuten.
          </p>
          <p>Aber zuerst: Wie heißt du?</p>
          <input
            className={styles.customerInput}
            type="text"
            autoFocus
            autoComplete="name"
            placeholder="Vor- und Nachname"
            value={draft.customerName}
            onChange={(e) =>
              setDraft({ ...draft, customerName: e.target.value })
            }
          />
        </>
      );
    case 'useCases':
      return (
        <MultiSelectQuestion
          title="Wofür brauchst du die Stimme?"
          subtitle="Mehrfachauswahl möglich. Wähle alle Einsatzbereiche aus, die passen."
          options={USE_CASES}
          value={draft.useCases}
          onChange={(v) => setDraft({ ...draft, useCases: v as UseCase[] })}
        />
      );
    case 'languages':
      return (
        <MultiSelectQuestion
          title="Welche Sprachen soll die Stimme abdecken?"
          subtitle="Mehrfachauswahl. Keine Pflicht-Einschränkung – Voices ohne Label können meistens auch andere Sprachen."
          options={LANGUAGES}
          value={draft.languages}
          onChange={(v) => setDraft({ ...draft, languages: v as Language[] })}
        />
      );
    case 'gender':
      return (
        <SingleSelectQuestion
          title="Soll die Stimme ein bestimmtes Geschlecht haben?"
          options={[...GENDERS, 'egal']}
          value={draft.gender}
          onChange={(v) =>
            setDraft({ ...draft, gender: v as Gender | 'egal' })
          }
        />
      );
    case 'ageRange':
      return (
        <SingleSelectQuestion
          title="Welcher Altersbereich passt?"
          options={[...AGE_RANGES, 'egal']}
          value={draft.ageRange}
          onChange={(v) =>
            setDraft({ ...draft, ageRange: v as AgeRange | 'egal' })
          }
        />
      );
    case 'characters':
      return (
        <MultiSelectQuestion
          title="Wie soll die Stimme charakterlich wirken?"
          subtitle={`Bis zu ${MAX_CHARACTERS} Eigenschaften. ${draft.characters.length} / ${MAX_CHARACTERS} gewählt.`}
          options={CHARACTERS}
          value={draft.characters}
          onChange={(v) =>
            setDraft({ ...draft, characters: v as Character[] })
          }
          max={MAX_CHARACTERS}
        />
      );
    case 'tonality':
      return (
        <MultiSelectQuestion
          title="Welche Tonalität passt zum Projekt?"
          subtitle="Mehrfachauswahl möglich."
          options={TONALITIES}
          value={draft.tonality}
          onChange={(v) =>
            setDraft({ ...draft, tonality: v as Tonality[] })
          }
        />
      );
  }
}

function MultiSelectQuestion({
  title,
  subtitle,
  options,
  value,
  onChange,
  max,
}: {
  title: string;
  subtitle?: string;
  options: readonly string[];
  value: string[];
  onChange: (v: string[]) => void;
  max?: number;
}) {
  function toggle(opt: string) {
    if (value.includes(opt)) {
      onChange(value.filter((v) => v !== opt));
    } else {
      if (max && value.length >= max) return;
      onChange([...value, opt]);
    }
  }
  return (
    <>
      <h2>{title}</h2>
      {subtitle && <p>{subtitle}</p>}
      <div className={styles.choicePills}>
        {options.map((opt) => {
          const selected = value.includes(opt);
          const atLimit = !!max && value.length >= max && !selected;
          return (
            <button
              key={opt}
              type="button"
              className={`${styles.choicePill} ${selected ? styles.selected : ''}`}
              disabled={atLimit}
              onClick={() => toggle(opt)}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </>
  );
}

function SingleSelectQuestion({
  title,
  options,
  value,
  onChange,
}: {
  title: string;
  options: readonly string[];
  value: string | null;
  onChange: (v: string) => void;
}) {
  return (
    <>
      <h2>{title}</h2>
      <div className={styles.choicePills}>
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            className={`${styles.choicePill} ${value === opt ? styles.selected : ''}`}
            onClick={() => onChange(opt)}
          >
            {opt}
          </button>
        ))}
      </div>
    </>
  );
}
